import { ReactNode, useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

interface PaymentStripeProviderProps {
  /**
   * Publishable key from `POST /me/payment-methods/setup-intent` (add-card) or
   * `GET /me/checkout/config` (checkout).
   *
   * The key is delivered per request rather than through a build-time env var — the API owns the
   * Stripe account configuration — so `loadStripe` cannot be called at module scope.
   */
  publishableKey: string;
  /**
   * Intent client secret, handed to Elements so the card fields bind to the right intent.
   *
   * Optional because checkout mounts Elements *before* any intent exists: the PaymentIntent is
   * only created once the customer presses pay, but the card iframes have to be on screen and
   * filled in before that. The split card Elements work standalone, so no secret is needed to
   * collect a card — it is only required at confirm time, which is a direct `stripe.confirm*` call
   * rather than an Elements option. Leave it unset in that case rather than passing a placeholder:
   * re-keying Elements later would remount the fields and discard what the customer typed.
   */
  clientSecret?: string;
  children: ReactNode;
}

/**
 * Wraps the card form in Stripe Elements.
 *
 * `loadStripe` is memoised per key so re-renders don't refetch the Stripe SDK.
 */
export function PaymentStripeProvider({
  publishableKey,
  clientSecret,
  children,
}: PaymentStripeProviderProps) {
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);

  // `options` is omitted entirely without a secret — passing `{ clientSecret: undefined }` puts
  // Elements into an invalid half-configured state instead of the standalone card-field mode.
  return (
    <Elements stripe={stripePromise} options={clientSecret ? { clientSecret } : undefined}>
      {children}
    </Elements>
  );
}

/**
 * Styling passed to each Stripe card Element so it matches `components/ui/input.tsx`.
 *
 * Stripe renders its fields inside a cross-origin iframe, so the project's Tailwind classes can't
 * reach them — the visual match has to be restated here as inline style objects. Values mirror
 * `Input`: 14px PP Neue Montreal, 0.5px letter spacing, neutral-1000 text, neutral-600 placeholder.
 */
export const STRIPE_ELEMENT_STYLE = {
  base: {
    fontFamily: '"PP Neue Montreal", sans-serif',
    fontSize: '14px',
    fontWeight: '400',
    letterSpacing: '0.5px',
    color: '#1a1a1a',
    lineHeight: '22px',
    '::placeholder': { color: '#a9a9a9' },
  },
  invalid: { color: '#1a1a1a', iconColor: '#971b2f' },
} as const;
